import React, { useEffect, useMemo, useState } from 'react';
import SignupContentWrapper from '../components/SignupContentWrapper';
import DoneeTypeCard from '../components/DoneeTypeCard';
import Grid from '@material-ui/core/Grid/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { OkButton } from '../components/TextFieldWrapper';
import { signSetAction } from '../../../store/signup/actions';
import { Organization } from '../../../store/signup/types';
import { Theme } from '@material-ui/core/styles';
import { AppState } from '../../../store';
import { PATH } from '../../../routes';
import { pushEvent } from '../../../utils/clevertapUtils';
import {
    CLICK_ORG_TYPE_PAGE_CHURCH_BTN,
    CLICK_ORG_TYPE_PAGE_NON_PROFIT_BTN,
    CLICK_ORG_TYPE_PAGE_OK_BTN,
    VIEW_ORG_TYPE_PAGE,
} from '../../../utils/clevertapEvents';
import { useRouteConfig } from '../utils/useRouteConfig';
import ribbonIcon from '../../../assets/ribbon-icon.svg';
import prayerIcon from '../../../assets/prayer-icon.svg';
import { Features } from 'store/featureFlags/types';
import { SEARCH_EXPERIMENT, SEARCH_UI_EXPERIMENT } from 'utils/clevertapVariantFlags';
import { useFullStoryIdentity } from '../utils/useFullStoryIdentity';

const STEP_INFO = 1;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        gridStyles: {
            '& .left, & .right': {
                display: 'flex',
                justifyContent: 'space-between',
                flexFlow: 'column',
            },
            '& .left': {
                marginRight: 25,
            },
            '& .right': {
                margin: '0 auto',
            },
            [theme.breakpoints.down('sm')]: {
                '& .margin-bottom': {
                    marginBottom: theme.spacing(3),
                },
                '& .left': {
                    margin: '0 auto',
                    marginBottom: theme.spacing(3),
                },
            },
        },
        subHeading: {
            marginTop: 32,
            color: '#73717F',
        },
    }),
);

const DoneeType: React.FC = () => {
    const { t } = useTranslation();
    const push = useNavigate();
    const dispatch = useAppDispatch();
    const { subHeading, gridStyles } = useStyles();
    const { defaultValue, fingerprint, fullStoryEnabled, newSearchUIEnabled, v2SearchAPIEnabled } = useSelector(
        (state: AppState) => ({
            defaultValue: state.Signup.doneeType,
            fingerprint: state.Signup.fingerprint,
            fullStoryEnabled: state.FeatureFlagging.features[Features.FULLSTORY],
            newSearchUIEnabled: state.FeatureFlagging.features[Features.SEARCH_UI_V2],
            v2SearchAPIEnabled: state.FeatureFlagging.features[Features.SEARCH_API_V2]
        }),
    );
    const [doneeType, setDoneeType] =
        useState<Organization['doneeType']>(defaultValue);
    const { next, previous } = useRouteConfig(PATH.SIGNUP.STEPS.TWO);
    useEffect(() => {
        pushEvent(VIEW_ORG_TYPE_PAGE, { 
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: newSearchUIEnabled ? 'on' : 'off', 
        });
    }, [newSearchUIEnabled, v2SearchAPIEnabled]);
    useFullStoryIdentity({
        enabled: fullStoryEnabled,
        fingerprint,
    });

    const onChurch = () => {
        pushEvent(CLICK_ORG_TYPE_PAGE_CHURCH_BTN);
        setDoneeType('church');
    };
    const onNonprofit = () => {
        pushEvent(CLICK_ORG_TYPE_PAGE_NON_PROFIT_BTN);
        setDoneeType('nonprofit');
    };
    const copy = useMemo(
        () => ({
            heading: t('signup.doneeType.heading'),
            subHeading: t('signup.doneeType.subHeading'),

            churchHeading: t('signup.doneeType.church.heading'),
            churchPointList: [
                t('signup.doneeType.church.point1'),
                t('signup.doneeType.church.point2'),
                t('signup.doneeType.church.point3'),
            ],

            nonprofitHeading: t('signup.doneeType.nonprofit.heading'),
            nonprofitPointList: [t('signup.doneeType.nonprofit.point1')],
        }),
        [t],
    );
    const onSubmit = () => {
        pushEvent(CLICK_ORG_TYPE_PAGE_OK_BTN);
        dispatch(
            signSetAction({
                doneeType,
            }),
        );
        next && push(next);
    };
    return (
        <SignupContentWrapper
            heading={copy.heading}
            stepInfo={STEP_INFO}
            showCloseButton={true}
            backUrl={previous}
        >
            <Grid container className={`${gridStyles} fs-unmask`}>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={6}
                    xl={6}
                    className="margin-bottom"
                >
                    <DoneeTypeCard
                        onClick={onChurch}
                        imgSrc={prayerIcon}
                        alt="Prayer Icon"
                        checked={doneeType === 'church'}
                        heading={copy.churchHeading}
                        pointList={copy.churchPointList}
                        className="left"
                        radioId="rdo-place-of-worship"
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <DoneeTypeCard
                        onClick={onNonprofit}
                        imgSrc={ribbonIcon}
                        alt="Ribbon Icon"
                        checked={doneeType === 'nonprofit'}
                        heading={copy.nonprofitHeading}
                        pointList={copy.nonprofitPointList}
                        className="right"
                        radioId="rdo-nonprofit"
                    />
                </Grid>
            </Grid>
            <div className={`${subHeading} fs-unmask`}>{copy.subHeading}</div>
            {!!doneeType && <OkButton onSubmit={onSubmit} />}
        </SignupContentWrapper>
    );
};

export default DoneeType;
